import React, { useMemo, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxLength?: number;
  required?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter your content...',
  disabled = false,
  className = '',
  maxLength = 3000,
  required = false
}) => {
  // Configure Quill modules and formats
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
    clipboard: {
      // Strip formatting when pasting
      matchVisual: false,
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'align'
  ];

  const getTextLength = (html: string) => {
    if (!html) return 0;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.length || 0;
  };

  const [currentLength, setCurrentLength] = useState(() => getTextLength(value));

  useEffect(() => {
    setCurrentLength(getTextLength(value));
  }, [value]);

  const handleChange = (content: string, _delta: any, source: string, editor: any) => {
    const textLen = (editor.getText()?.length ?? 1) - 1;
    setCurrentLength(textLen);
    if (source !== 'user') return;
    if (textLen <= maxLength) {
      onChange(content);
    }
  };
  const isNearLimit = currentLength > maxLength * 0.8;
  const isOverLimit = currentLength > maxLength;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Rich Text Editor */}
      <div className={`
        card p-0 border overflow-hidden flex flex-col h-full
        ${isOverLimit ? 'border-red-300 dark:border-red-600' : ''}
        ${disabled ? 'opacity-50' : ''}
      `}>
        <ReactQuill
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={disabled}
          className="flex-1 overflow-y-auto quill-scrollable"
          style={{
            minHeight: '120px',
            maxHeight: '200px',
          }}
        />
      </div>

      {/* Character Count and Validation */}
      <div className="flex justify-between items-center text-sm">
        <div className="flex items-center space-x-4">
          {required && !value && (
            <span className="text-red-500 dark:text-red-400 flex items-center">
              <span className="mr-1">*</span>
              Required field
            </span>
          )}
          {isOverLimit && (
            <span className="text-red-500 dark:text-red-400">
              Content exceeds maximum length
            </span>
          )}
        </div>
        
        <span className={`
          ${isOverLimit ? 'text-red-500 dark:text-red-400' : 
            isNearLimit ? 'text-yellow-600 dark:text-yellow-400' : 
            'text-neutral-500 dark:text-text-dark-tertiary'}
        `}>
          {currentLength} / {maxLength}
        </span>
      </div>

      {/* Help Text */}
      <div className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
        <p>💡 Use the toolbar to format your text. You can add headers, lists, links, and more.</p>
      </div>
    </div>
  );
};

export default RichTextEditor;
